
import 'normalize.css'
import { defineComponent, reactive, toRefs, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { DialogEvent } from '@/enums/eventType'
import { DialogShowEventArgs } from '@/types/dialog'
import { emitter } from '@/helpers/eventBusHelper'
import { handleErrorToast } from '@/helpers/errorHandler'
import { kakaoSdkKey } from '@/constants/sdk'
import { useRoute } from 'vue-router'
import * as naviService from '@/services/naviService'
import HorizontalButtonDialog from '@/components/composite/HorizontalButtonDialog.vue'
import router from '@/router'
import store, { Action, MutationName } from '@/store'
import VerticalButtonDialog from '@/components/composite/VerticalButtonDialog.vue'
import Loading from '@/components/composite/Loading.vue'
import * as documentService from '@/services/documentService'

declare global {
  const postscribe: any
}

export default defineComponent({
  components: {
    VerticalButtonDialog,
    HorizontalButtonDialog,
    Loading,
  },
  setup() {
    const dialog = ref<DialogShowEventArgs | null>(null)
    const state = reactive({
      isLoaded: false,
      isVerticalDialogVisible: false,
      isHorizontalDialogVisible: false,
    })

    const route = useRoute()

    watch(() => route.path, (path) => {
      if (path === '/qr-scan') {
        store.commit(MutationName.IS_DARK_TOAST, true)
      } else {
        store.commit(MutationName.IS_DARK_TOAST, false)
      }
    })
    store.dispatch(Action.GET_ME).then(async (isLoggedIn) => {
      if (isLoggedIn) {
        await store.dispatch(Action.GET_DEVICE_PARTS)
        if (route.path.match(/\/login/) || route.matched.length === 0) {
          router.replace('/')
        }
      } else {
        router.replace({
          path: '/login',
          query: {
            redirectUri: route.query?.redirectUri,
          },
        })
      }

      try {
        await Promise.all([
          new Promise((resolve) => {
            postscribe(
              document.body,
              `<script src="//dapi.kakao.com/v2/maps/sdk.js?appkey=${kakaoSdkKey}&libraries=services"><` + '/script>',
              {
                done: resolve,
              }
            )
          }),
          new Promise((resolve) => {
            postscribe(document.body, '<script src="//developers.kakao.com/sdk/js/kakao.min.js"><' + '/script>', {
              done: () => {
                naviService.init()
                resolve(null)
              },
            })
          }),
        ])
      } catch (e: any) {
        handleErrorToast(e)
      } finally {
        state.isLoaded = true
      }
    })

    const dialogShowVerticalHandler = (dialogShowEventArgs?: DialogShowEventArgs) => {
      if (dialogShowEventArgs) {
        dialog.value = dialogShowEventArgs
        state.isVerticalDialogVisible = true
      }
    }

    const dialogShowHorizontalHandler = (dialogShowEventArgs?: DialogShowEventArgs) => {
      if (dialogShowEventArgs) {
        dialog.value = dialogShowEventArgs
        state.isHorizontalDialogVisible = true
      }
    }

    const dialogHideHandler = () => {
      state.isVerticalDialogVisible = false
      state.isHorizontalDialogVisible = false
    }

    onMounted(() => {
      emitter.on(DialogEvent.SHOW_VERTICAL, dialogShowVerticalHandler)
      emitter.on(DialogEvent.SHOW_HORIZONTAL, dialogShowHorizontalHandler)
      emitter.on(DialogEvent.HIDE, dialogHideHandler)

      documentService.init()
    })

    onBeforeUnmount(() => {
      emitter.off(DialogEvent.SHOW_VERTICAL, dialogShowVerticalHandler)
      emitter.off(DialogEvent.SHOW_HORIZONTAL, dialogShowHorizontalHandler)
      emitter.off(DialogEvent.HIDE, dialogHideHandler)
    })

    return {
      ...toRefs(state),
      dialog,
      emitter,
      DialogEvent,
    }
  },
})
